import { Component, FC, useEffect, useState } from 'react';
import { DatePicker } from 'antd';
import { RangePickerProps } from 'antd/lib/date-picker/generatePicker';
import { CommonPickerMethods } from 'antd/lib/date-picker/generatePicker/interface';
import moment, { Moment } from 'moment';

declare type EventValue<Moment> = Moment | null;
export declare type RangeValue<Moment> = [EventValue<Moment>, EventValue<Moment>] | null;

const { RangePicker } = DatePicker;

export const CustomDatePicker: FC<{
    dates?: RangeValue<Moment>;
    setDates?: (dates?: RangeValue<Moment>) => void;
    onDatesSelected: (startDate: string, endDate: string) => void;
    noOfDays: number;
    enableFuture?: boolean;
    disablePast?: boolean;
    className?: string;
}> = ({ onDatesSelected, dates, setDates, noOfDays, enableFuture, className, disablePast }) => {
    let datePickerRef:
        | (Component<RangePickerProps<moment.Moment>> & CommonPickerMethods)
        | null = null;

    const [value, setValue] = useState<RangeValue<Moment> | undefined>(dates);
    const [open, setOpen] = useState<boolean>(false);

    useEffect(() => {
        if (!dates) {
            setValue(undefined);
        }
    }, [dates]);

    const ref = (
        ref: (Component<RangePickerProps<moment.Moment>> & CommonPickerMethods) | null,
    ): void => {
        datePickerRef = ref;
    };

    const onCalendarChange = (val: RangeValue<Moment>) => {
        setDates && setDates(val);
        if (val?.[0] && val[1]) {
            onDatesSelected(val[0].startOf('day').toISOString(), val[1].endOf('day').toISOString());
            datePickerRef && datePickerRef.blur();
        }
    };

    const onOpenChange = (open: boolean) => {
        if (open) {
            setOpen(true);
            setDates && setDates(([] as unknown) as RangeValue<Moment>);
        } else {
            setOpen(false);
        }
    };

    const disabledDate = (current: Moment) => {
        const tooLate = dates && dates[0] && current.diff(dates[0], 'day') >= noOfDays;
        const tooEarly = dates && dates[1] && dates[1].diff(current, 'day') >= noOfDays;
        const isFuture = !enableFuture && current > moment().endOf('day');
        const isPast = disablePast && current < moment().startOf('day');

        return tooEarly || tooLate || isFuture || isPast ? true : false;
    };

    return (
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        <RangePicker
            ref={ref}
            value={open ? (([] as unknown) as RangeValue<Moment>) : value}
            disabledDate={disabledDate}
            size="small"
            inputReadOnly={true}
            popupStyle={{ fontFamily: 'Lato' }}
            className={className}
            onCalendarChange={onCalendarChange}
            onChange={(dates) => {
                setValue(dates);
            }}
            bordered={false}
            onOpenChange={onOpenChange}
            allowClear={false}
            format={(date) => moment(date).format('D MMM YYYY')}
        />
    );
};
